import { useEffect, useRef } from 'react';
import 'tailwindcss/tailwind.css';

interface PhoneDialingAnimationProps {
  width: string;
  height: string;
  isDialing: boolean;
}

function PhoneDialingAnimation({ width, height, isDialing }: PhoneDialingAnimationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    if (!isDialing) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    let animationFrameId: number;
    let pulseSize = 0;
    let growing = true;
    const maxSize = Math.min(canvas.width, canvas.height) / 2 - 10;
    const minSize = maxSize * 0.6;
    const pulseSpeed = 0.5;
    
    const draw = () => {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw outer circle (phone outline)
      ctx.beginPath();
      ctx.arc(canvas.width / 2, canvas.height / 2, maxSize, 0, Math.PI * 2);
      ctx.strokeStyle = '#e5e7eb'; // Light gray
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Draw pulsing circle
      ctx.beginPath();
      ctx.arc(canvas.width / 2, canvas.height / 2, pulseSize, 0, Math.PI * 2);
      
      // Create gradient for pulsing circle
      const gradient = ctx.createRadialGradient(
        canvas.width / 2, canvas.height / 2, 0,
        canvas.width / 2, canvas.height / 2, pulseSize
      );
      gradient.addColorStop(0, 'rgba(220, 38, 38, 0.8)'); // Red core
      gradient.addColorStop(0.7, 'rgba(220, 38, 38, 0.4)');
      gradient.addColorStop(1, 'rgba(220, 38, 38, 0.1)');
      
      ctx.fillStyle = gradient;
      ctx.fill();
      
      // Update pulse size
      if (growing) {
        pulseSize += pulseSpeed;
        if (pulseSize >= maxSize) {
          growing = false;
        }
      } else {
        pulseSize -= pulseSpeed;
        if (pulseSize <= minSize) {
          growing = true;
        }
      }
      
      // Draw phone icon in the center
      ctx.beginPath();
      const phoneSize = maxSize * 0.3;
      
      // Simple phone icon (receiver)
      ctx.moveTo(canvas.width / 2 - phoneSize / 2, canvas.height / 2 - phoneSize / 4);
      ctx.quadraticCurveTo(
        canvas.width / 2 - phoneSize / 2, canvas.height / 2 + phoneSize / 2,
        canvas.width / 2, canvas.height / 2 + phoneSize / 2
      );
      ctx.quadraticCurveTo(
        canvas.width / 2 + phoneSize / 2, canvas.height / 2 + phoneSize / 2,
        canvas.width / 2 + phoneSize / 2, canvas.height / 2 - phoneSize / 4
      );
      ctx.quadraticCurveTo(
        canvas.width / 2 + phoneSize / 2, canvas.height / 2 - phoneSize / 2,
        canvas.width / 2, canvas.height / 2 - phoneSize / 2
      );
      ctx.quadraticCurveTo(
        canvas.width / 2 - phoneSize / 2, canvas.height / 2 - phoneSize / 2,
        canvas.width / 2 - phoneSize / 2, canvas.height / 2 - phoneSize / 4
      );
      
      ctx.fillStyle = 'white';
      ctx.fill();
      ctx.strokeStyle = '#dc2626'; // Red
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Continue animation
      animationFrameId = requestAnimationFrame(draw);
    };
    
    draw();
    
    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [isDialing, width, height]);
  
  return (
    <div className="flex justify-center items-center">
      <canvas 
        ref={canvasRef} 
        width={width} 
        height={height} 
        className={`bg-white rounded-full ${!isDialing ? 'hidden' : ''}`}
      />
    </div>
  );
}

export default PhoneDialingAnimation;
